import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip
} from '@mui/material';
import { Upload as UploadIcon, Download as DownloadIcon, SmartToy as AIIcon } from '@mui/icons-material';
import api from '../utils/api';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const XLSX = require('xlsx');

interface AIBulkTrainingDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TrainingPreview {
  title: string;
  description?: string;
  target_audience?: string;
  regulatory_standards?: string;
  duration_minutes?: number;
  video_url?: string;
}

interface BulkJobStatus {
  job_id: string;
  total_count: number;
  completed_count: number;
  failed_count: number;
  status: string;
  current_training_title: string;
  current_step: string;
  error_log: Array<{row: number; title: string; error: string}>;
  created_trainings: number[];
}


const AIBulkTrainingDialog: React.FC<AIBulkTrainingDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<TrainingPreview[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [jobStatus, setJobStatus] = useState<BulkJobStatus | null>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (polling && jobStatus) {
      intervalId = setInterval(async () => {
        try {
          const response = await api.get(`/bulk-generation/bulk_job_status/?job_id=${jobStatus.job_id}`);
          setJobStatus(response.data);
          
          if (response.data.status === 'completed') {
            setPolling(false);
            onSuccess();
          }
        } catch (error) {
          console.error('Error polling job status:', error);
          setPolling(false);
        }
      }, 2000); // Poll every 2 seconds
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [polling, jobStatus, onSuccess]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setErrors([]);
      setPreview([]);
      setJobStatus(null);
      
      // Validate file type
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setErrors(['Please select an Excel file (.xlsx or .xls)']);
        return;
      }
      
      // Parse the Excel file to show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          const previewData: TrainingPreview[] = jsonData.map((row: any) => ({
            title: row.title || '',
            description: row.description || '',
            target_audience: row.target_audience || '',
            regulatory_standards: row.regulatory_standards || '',
            duration_minutes: row.duration_minutes || 15,
            video_url: row.video_url || ''
          })).filter((item: TrainingPreview) => item.title.trim() !== '');
          
          setPreview(previewData);
        } catch (error) {
          console.error('Excel parsing error:', error);
          setErrors(['Failed to parse Excel file. Please check the file format.']);
        }
      };
      
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const handleUpload = async (retryCount = 0) => {
    if (!file) return;

    setUploading(true);
    setErrors([]);

    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/bulk-generation/bulk_generate/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setJobStatus(response.data);
      setPolling(true); // Start polling for status updates
      setUploading(false);
    } catch (error: any) {
      console.error('Upload error:', error);
      
      const status = error.response?.status;
      const isRetryable = error.response?.data?.retryable || 
                         status === 503 || 
                         (status === 500 && error.response?.data?.error?.toLowerCase().includes('database'));
      
      // Retry on database connection errors
      if (isRetryable && retryCount < maxRetries) {
        const delay = retryDelay * (retryCount + 1); // Exponential backoff
        setErrors([
          `Database connection issue detected. Retrying in ${delay / 1000} seconds... (Attempt ${retryCount + 1}/${maxRetries})`
        ]);
        
        setTimeout(() => {
          handleUpload(retryCount + 1);
        }, delay);
        return;
      }
      
      // Show error message
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          'Upload failed. Please check your file and try again.';
      
      setErrors([errorMessage]);
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setErrors([]);
    setJobStatus(null);
    setPolling(false);
    onClose();
  };

  const downloadTemplate = async () => {
    try {
      setErrors([]);
      const response = await api.get('/bulk-generation/download_bulk_template/', {
        responseType: 'blob',
      });
      
      // Check if response is actually a blob
      if (response.data instanceof Blob && response.data.size > 0) {
        // Check content type to see if it's an error JSON response
        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
          // It's an error response, parse it
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          setErrors([errorData.error || errorData.detail || 'Failed to download template']);
        } else {
          // It's a valid Excel file
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'bulk_training_template.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } else {
        setErrors(['Failed to download template: Invalid response']);
      }
    } catch (error: any) {
      console.error('Template download error:', error);
      // Handle error response
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          // Error response is a blob, try to parse it
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            setErrors([errorData.error || errorData.detail || 'Failed to download template']);
          } catch {
            setErrors([`Failed to download template (Status: ${error.response.status})`]);
          }
        } else if (typeof error.response.data === 'object') {
          setErrors([error.response.data.error || error.response.data.detail || 'Failed to download template']);
        } else {
          setErrors([`Failed to download template (Status: ${error.response.status})`]);
        }
      } else {
        const errorMessage = error.message || 'Failed to download template. Please check your connection and try again.';
        setErrors([errorMessage]);
      }
    }
  };

  const progress = jobStatus 
    ? Math.round((jobStatus.completed_count / jobStatus.total_count) * 100)
    : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon />
          <Typography variant="h6">Bulk AI Training Generation</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {!jobStatus ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Upload an Excel file with training titles to generate multiple trainings automatically.
                </Typography>
                <Typography variant="body2">
                  <strong>Required column:</strong> title
                </Typography>
                <Typography variant="body2">
                  <strong>Optional columns:</strong> description, target_audience, regulatory_standards, duration_minutes, video_url
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Note: If description is empty, it will be automatically generated using AI. If video_url is provided, a video section will be added before the final assessment.
                </Typography>
              </Alert>

              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                >
                  Select Excel File
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                  />
                </Button>

                <Button
                  variant="text"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                >
                  Download Template
                </Button>

                {file && (
                  <Typography variant="body2" sx={{ ml: 'auto' }}>
                    Selected: {file.name}
                  </Typography>
                )}
              </Box>

              {preview.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Preview ({preview.length} trainings)
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Target Audience</TableCell>
                          <TableCell>Regulatory Standards</TableCell>
                          <TableCell>Video</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preview.map((training, index) => (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{training.title}</TableCell>
                            <TableCell>
                              {training.description ? (
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {training.description}
                                </Typography>
                              ) : (
                                <Chip label="Will be AI-generated" size="small" color="primary" />
                              )}
                            </TableCell>
                            <TableCell>{training.target_audience || '-'}</TableCell>
                            <TableCell>{training.regulatory_standards || '-'}</TableCell>
                            <TableCell>
                              {training.video_url ? (
                                <Chip label="✓ Video included" size="small" color="success" />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {errors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Errors
                  </Typography>
                  <ul>
                    {errors.map((error, index) => (
                      <li key={index}>
                        <Typography variant="body2">{error}</Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Generation Progress
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    {jobStatus.completed_count} of {jobStatus.total_count} trainings completed
                  </Typography>
                  <Typography variant="body2">{progress}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
              </Box>

              {jobStatus.status === 'processing' && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body1" fontWeight="medium">
                      Processing...
                    </Typography>
                  </Box>
                  
                  {jobStatus.current_training_title && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current Training:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" gutterBottom>
                        {jobStatus.current_training_title}
                      </Typography>
                      {jobStatus.current_step && (
                        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                          {jobStatus.current_step}
                        </Typography>
                      )}
                    </Paper>
                  )}
                </Box>
              )}

              {jobStatus.status === 'completed' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body1">
                    Successfully generated {jobStatus.completed_count} trainings!
                  </Typography>
                  {jobStatus.failed_count > 0 && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {jobStatus.failed_count} trainings failed to generate. See details below.
                    </Typography>
                  )}
                </Alert>
              )}

              {jobStatus.failed_count > 0 && jobStatus.error_log.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom color="error">
                    Failed Trainings
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobStatus.error_log.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell>{error.row}</TableCell>
                            <TableCell>{error.title}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="error">
                                {error.error}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {!jobStatus || jobStatus.status === 'completed' ? (
          <>
            <Button onClick={handleClose}>
              {jobStatus ? 'Close' : 'Cancel'}
            </Button>
            {!jobStatus && (
              <Button
                onClick={() => handleUpload(0)}
                variant="contained"
                disabled={!file || uploading || preview.length === 0}
                startIcon={uploading ? <CircularProgress size={20} /> : <AIIcon />}
              >
                {uploading ? 'Starting...' : 'Generate Trainings'}
              </Button>
            )}
          </>
        ) : (
          <Button onClick={handleClose} disabled>
            Generating...
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIBulkTrainingDialog;

